import { Component, ReactNode } from "react";
import { Home } from "lucide-react";
import { StandardAction, StandardState } from "@/components/standard";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught render error:', {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#0e0e0e] px-4 py-12 sm:px-6">
          <StandardState
            kind="error"
            surface="dark"
            title="The Standard needs a reload"
            description="The page ran into a problem. Your saved work is still protected. Reload the app or return home."
            actionLabel="Reload"
            onAction={() => window.location.reload()}
            secondaryAction={(
              <StandardAction variant="secondary" onClick={() => { window.location.href = '/'; }} className="border-white/15 bg-white/[0.04] text-white hover:bg-white/[0.08]">
                <Home className="size-4" aria-hidden="true" /> Home
              </StandardAction>
            )}
            className="w-full max-w-md"
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
